import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { useLoanTypes, useCreateLoanType, useUpdateLoanType, useDeleteLoanType } from '../hooks/useLoanTypes.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const typeOptions = [
  { value: 'SIMPLE', label: 'Simple' },
  { value: 'REDUCING', label: 'Reducing' },
  { value: 'FIXED', label: 'Fixed' },
];

const durationUnitOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const guarantorThresholdTypeOptions = [
  { value: 'savings_percentage', label: 'Percentage of Savings' },
  { value: 'loan_percentage', label: 'Percentage of Loan' },
];

const documentTypes = [
  { value: 'application', label: 'Loan Application Form' },
  { value: 'identity', label: 'Identity Document' },
  { value: 'income_proof', label: 'Income Proof' },
  { value: 'collateral', label: 'Collateral Documentation' },
  { value: 'guarantor', label: 'Guarantor Information' },
  { value: 'other', label: 'Other Documents' },
];

export default function LoansTypesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLoanType, setSelectedLoanType] = useState(null);

  const { data: loanTypesData, isLoading } = useLoanTypes();
  const createLoanType = useCreateLoanType();
  const updateLoanType = useUpdateLoanType();
  const deleteLoanType = useDeleteLoanType();
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();

  const columns = [
    { label: 'Name', field: 'name', sortable: true },
    { label: 'Description', field: 'description', sortable: true },
    { label: 'Monthly Interest', field: 'monthly_interest', sortable: true, render: row => `${row.monthly_interest}%` },
    { label: 'Type', field: 'type', sortable: true },
    { 
      label: 'Duration', 
      field: 'duration', 
      sortable: true, 
      render: row => row.duration && row.duration_unit ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {row.duration} {row.duration_unit === 'months' ? (row.duration > 1 ? 'months' : 'month') : row.duration_unit}
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Set
        </span>
      )
    },
    { 
      label: 'Guarantors', 
      field: 'requires_guarantors', 
      sortable: true, 
      render: row => row.requires_guarantors ? (
        <div className="flex items-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Required
          </span>
          {row.guarantor_threshold_type && (
            <span className="ml-2 text-xs text-gray-500">
              ({row.guarantor_threshold_type === 'savings_percentage' ? 'Savings %' : 'Loan %'})
            </span>
          )}
        </div>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Required
        </span>
      )
    },
    { 
      label: 'Documents Required', 
      field: 'require_documents', 
      sortable: true, 
      render: row => row.require_documents ? (
        <div className="flex items-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Yes
          </span>
          {row.required_document_categories && row.required_document_categories.length > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              ({row.required_document_categories.length} categories)
            </span>
          )}
        </div>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          No
        </span>
      )
    },
    {
      label: 'Actions',
      field: 'actions',
      render: (row) => (
        <div className="flex space-x-2">
          {can(PERMISSIONS.MANAGE_LOAN_TYPES) && (
            <>
              <button className="text-blue-600 hover:text-blue-800" onClick={() => { setSelectedLoanType(row); setIsModalOpen(true); }}><Pencil size={18} /></button>
              {can(PERMISSIONS.DELETE_LOAN_TYPE) && (
                <button className="text-red-600 hover:text-red-800" onClick={() => handleDeleteLoanType(row.id)}><Trash2 size={18} /></button>
              )}
            </>
          )}
        </div>
      )
    },
  ];

  const handleCreateOrUpdateLoanType = (loanType) => {
    if (selectedLoanType) {
      updateLoanType.mutate({ id: selectedLoanType.id, ...loanType }, { onSuccess: () => { setIsModalOpen(false); setSelectedLoanType(null); } });
    } else {
      createLoanType.mutate(loanType, { onSuccess: () => { setIsModalOpen(false); } });
    }
  };

  const handleDeleteLoanType = (id) => {
    if (window.confirm('Are you sure you want to delete this loan type?')) {
      deleteLoanType.mutate(id);
    }
  };

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_LOAN_TYPES}>
      <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h2 className="text-2xl font-bold">Loan Types</h2>
        <PermissionGate permission={PERMISSIONS.MANAGE_LOAN_TYPES}>
          <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto" onClick={() => { setSelectedLoanType(null); setIsModalOpen(true); }}>
            <Plus size={16} className="mr-2" /> Add Loan Type
          </button>
        </PermissionGate>
      </div>
      <Table columns={columns} data={loanTypesData?.data || []} loading={isLoading} />
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setSelectedLoanType(null); }}
        size="xl"
      >
        <LoanTypeForm
          initialData={selectedLoanType}
          onSubmit={handleCreateOrUpdateLoanType}
          onCancel={() => { setIsModalOpen(false); setSelectedLoanType(null); }}
        />
      </Modal>
    </div>
    </PermissionPage>
  );
}

function LoanTypeForm({ initialData, onSubmit, onCancel }) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [monthlyInterest, setMonthlyInterest] = useState(initialData?.monthly_interest || '');
  const [type, setType] = useState(initialData?.type || 'SIMPLE');
  
  // Duration states
  const [duration, setDuration] = useState(initialData?.duration || '');
  const [durationUnit, setDurationUnit] = useState(initialData?.duration_unit || 'months');
  
  // Guarantor states
  const [requiresGuarantors, setRequiresGuarantors] = useState(initialData?.requires_guarantors || false);
  const [guarantorThresholdType, setGuarantorThresholdType] = useState(initialData?.guarantor_threshold_type || 'savings_percentage');
  const [guarantorThresholdValue, setGuarantorThresholdValue] = useState(initialData?.guarantor_threshold_value || '0.7');
  
  // Document requirement states
  const [requireDocuments, setRequireDocuments] = useState(initialData?.require_documents || false);
  const [requiredDocumentCategories, setRequiredDocumentCategories] = useState(initialData?.required_document_categories || []);
  const [documentRequirementsDescription, setDocumentRequirementsDescription] = useState(initialData?.document_requirements_description || '');

  const toggleDocumentCategory = (category) => {
    setRequiredDocumentCategories(prev => 
      prev.includes(category) 
        ? prev.filter(cat => cat !== category)
        : [...prev, category]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ 
      name, 
      description, 
      monthly_interest: monthlyInterest, 
      type,
      duration: duration ? parseInt(duration) : null,
      duration_unit: duration ? durationUnit : null,
      requires_guarantors: requiresGuarantors,
      guarantor_threshold_type: requiresGuarantors ? guarantorThresholdType : null,
      guarantor_threshold_value: requiresGuarantors ? parseFloat(guarantorThresholdValue) : null,
      require_documents: requireDocuments,
      required_document_categories: requireDocuments ? requiredDocumentCategories : [],
      document_requirements_description: requireDocuments ? documentRequirementsDescription : ''
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Name</label>
        <input type="text" className="w-full border rounded px-3 py-2" value={name} onChange={e => setName(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <input type="text" className="w-full border rounded px-3 py-2" value={description} onChange={e => setDescription(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Monthly Interest (%)</label>
        <input type="number" className="w-full border rounded px-3 py-2" value={monthlyInterest} onChange={e => setMonthlyInterest(e.target.value)} required min={0} step={0.01} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Type</label>
        <select className="w-full border rounded px-3 py-2" value={type} onChange={e => setType(e.target.value)} required>
          {typeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      {/* Duration Section */}
      <div className="border-t pt-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Loan Duration (Optional)</h3>
          <p className="text-xs text-gray-600">
            Set a fixed duration for this loan type. If left empty, users will be able to choose their own duration when applying for loans.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Duration</label>
            <input 
              type="number" 
              className="w-full border rounded px-3 py-2" 
              value={duration} 
              onChange={e => setDuration(e.target.value)} 
              min={1}
              placeholder="e.g., 4"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Duration Unit</label>
            <select className="w-full border rounded px-3 py-2" value={durationUnit} onChange={e => setDurationUnit(e.target.value)}>
              {durationUnitOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>
        
        {duration ? (
          <div className="mt-3 text-sm text-gray-700 bg-blue-50 border border-blue-200 p-3 rounded">
            <strong>Fixed Duration:</strong> All loans of this type will automatically have a duration of {duration} {durationUnit === 'months' ? (parseInt(duration) > 1 ? 'months' : 'month') : durationUnit}. Users won't be able to change this when applying.
          </div>
        ) : (
          <div className="mt-3 text-sm text-gray-700 bg-yellow-50 border border-yellow-200 p-3 rounded">
            <strong>Flexible Duration:</strong> Users will be able to choose their own loan duration when applying for this loan type.
          </div>
        )}
      </div>

      {/* Guarantor Section */}
      <div className="border-t pt-4">
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id="requiresGuarantors"
            checked={requiresGuarantors}
            onChange={(e) => setRequiresGuarantors(e.target.checked)}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="requiresGuarantors" className="text-sm font-medium text-gray-700">
            Require guarantors for this loan type
          </label>
        </div>

        {requiresGuarantors && (
          <div className="space-y-4 ml-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Threshold Type</label>
                <select 
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  value={guarantorThresholdType}
                  onChange={(e) => setGuarantorThresholdType(e.target.value)}
                >
                  {guarantorThresholdTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold Value ({guarantorThresholdType === 'savings_percentage' ? 'Percentage' : 'Percentage'})
                </label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  value={guarantorThresholdValue}
                  onChange={(e) => setGuarantorThresholdValue(e.target.value)}
                  min={0}
                  max={1}
                  step={0.1}
                  placeholder="e.g., 0.7"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600">
              {guarantorThresholdType === 'savings_percentage' 
                ? `Guarantors will be required for amounts above ${(parseFloat(guarantorThresholdValue) * 100).toFixed(0)}% of the member's savings`
                : `Guarantors will be required for amounts above ${(parseFloat(guarantorThresholdValue) * 100).toFixed(0)}% of the loan amount`
              }
            </div>
          </div>
        )}
      </div>

      {/* Document Requirements Section */}
      <div className="border-t pt-4">
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id="requireDocuments"
            checked={requireDocuments}
            onChange={(e) => setRequireDocuments(e.target.checked)}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="requireDocuments" className="text-sm font-medium text-gray-700">
            Require document attachments for this loan type
          </label>
        </div>

        {requireDocuments && (
          <div className="space-y-4 ml-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Document Categories</label>
              <div className="grid grid-cols-2 gap-2">
                {documentTypes.map(type => (
                  <label key={type.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={requiredDocumentCategories.includes(type.value)}
                      onChange={() => toggleDocumentCategory(type.value)}
                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Requirements Description</label>
              <textarea
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                value={documentRequirementsDescription}
                onChange={(e) => setDocumentRequirementsDescription(e.target.value)}
                rows={3}
                placeholder="Describe what documents are required for this loan type..."
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        <button type="button" className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
 onClick={onCancel}>Cancel</button>
        <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">{initialData ? 'Update' : 'Add'} Loan Type</button>
      </div>
    </form>
  );
} 